import { useQuery } from "@tanstack/react-query";
import { type Address } from "viem";
import { api } from "@/lib/api";

export type AllowlistAuth = {
  to: Address;
  maxQty: string;
  priceWei: string;
  deadline: number;
};

export type AllowlistResponse = {
  eligible: boolean;
  auth?: AllowlistAuth;
  signature?: `0x${string}`;
};

/**
 * Calls the worker's `/api/drops/:c/:a/allowlist/:wallet/sign` endpoint to
 * fetch a presale signature for `wallet`. Only fires when `enabled` is true
 * — typically gated on (valid params && wallet connected && in presale).
 *
 * Returns the raw response plus a derived `eligible` boolean for callers
 * that don't want to inspect the auth/signature pair themselves.
 */
export function useAllowlistSign({
  chainId,
  address,
  wallet,
  enabled,
}: {
  chainId: number | undefined;
  address: string;
  wallet: Address | undefined;
  enabled: boolean;
}): { response: AllowlistResponse | undefined; eligible: boolean } {
  const { data: response } = useQuery({
    queryKey: ["allowlist", chainId, address, wallet],
    queryFn: () =>
      api.get<AllowlistResponse>(`/api/drops/${chainId}/${address}/allowlist/${wallet}/sign`),
    enabled,
    staleTime: 60_000,
  });
  const eligible = !!response?.eligible && !!response.auth && !!response.signature;
  return { response, eligible };
}
